

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SkillCategoryBadge } from "@/components/skill-category-badge";
import type { SkillCategory } from "@/lib/session-planning";
import { CATEGORY_DISPLAY } from "@/lib/session-planning";
import { cn } from "@/lib/utils";
import { ClipboardList } from "lucide-react";

type SessionPlanEntry = {
  skillKey: string;
  category: SkillCategory;
  exerciseCount: number;
  skillName: string;
};

type SessionPlanPreviewProps = {
  plan: SessionPlanEntry[];
  className?: string;
  compact?: boolean;
};

export function SessionPlanPreview({
  plan,
  className,
  compact = false,
}: SessionPlanPreviewProps) {
  if (plan.length === 0) return null;

  // Group by category for display order
  const categoryOrder: SkillCategory[] = ["new", "struggling", "learning", "review"];
  const sortedPlan = [...plan].sort((a, b) => {
    const aIndex = categoryOrder.indexOf(a.category);
    const bIndex = categoryOrder.indexOf(b.category);
    return aIndex - bIndex;
  });

  // Calculate total exercises
  const totalExercises = plan.reduce((sum, entry) => sum + entry.exerciseCount, 0);

  if (compact) {
    return (
      <div className={cn("flex flex-wrap gap-2", className)}>
        {sortedPlan.map((entry) => (
          <div
            key={entry.skillKey}
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded-md text-sm",
              CATEGORY_DISPLAY[entry.category].bgColor,
              CATEGORY_DISPLAY[entry.category].borderColor,
              "border"
            )}
          >
            <span>{CATEGORY_DISPLAY[entry.category].emoji}</span>
            <span className="font-medium truncate max-w-[150px]">{entry.skillName}</span>
            <span className="text-xs text-muted-foreground">({entry.exerciseCount})</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Card className={cn("border-primary/20", className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-primary" />
          Sesijos planas
          <span className="text-sm font-normal text-muted-foreground ml-auto">
            {totalExercises} {totalExercises === 1 ? "pratimas" :
              totalExercises < 10 ? "pratimai" : "pratimų"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {sortedPlan.map((entry) => {
          const display = CATEGORY_DISPLAY[entry.category];
          return (
            <div
              key={entry.skillKey}
              className={cn(
                "flex items-center justify-between p-3 rounded-lg border",
                display.bgColor,
                display.borderColor
              )}
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{display.emoji}</span>
                <div>
                  <p className={cn("font-medium", display.color)}>
                    {entry.skillName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {display.description}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-semibold">
                  {entry.exerciseCount} {entry.exerciseCount === 1 ? "pratimas" :
                    entry.exerciseCount < 5 ? "pratimai" : "pratimų"}
                </p>
                <SkillCategoryBadge category={entry.category} size="sm" showEmoji={false} />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

type SessionPlanSummaryProps = {
  plan: SessionPlanEntry[];
  skillChanges?: Array<{
    skillKey: string;
    skillName: string;
    oldProficiency: number;
    newProficiency: number;
    change: number;
  }>;
  className?: string;
};

export function SessionPlanSummary({
  plan,
  skillChanges = [],
  className,
}: SessionPlanSummaryProps) {
  if (plan.length === 0) return null;

  // Group changes by category
  const changesByCategory = new Map<SkillCategory, typeof skillChanges>();

  for (const entry of plan) {
    const change = skillChanges.find((sc) => sc.skillKey === entry.skillKey);
    const existing = changesByCategory.get(entry.category) || [];
    if (change) {
      existing.push(change);
    } else {
      // Create placeholder for skills without changes
      existing.push({
        skillKey: entry.skillKey,
        skillName: entry.skillName,
        oldProficiency: 0,
        newProficiency: 0,
        change: 0,
      });
    }
    changesByCategory.set(entry.category, existing);
  }

  const categoryOrder: SkillCategory[] = ["new", "struggling", "learning", "review"];

  return (
    <div className={cn("space-y-4", className)}>
      {categoryOrder.map((category) => {
        const changes = changesByCategory.get(category);
        if (!changes || changes.length === 0) return null;

        const display = CATEGORY_DISPLAY[category];

        return (
          <div key={category} className="space-y-2">
            <h4 className={cn("font-medium flex items-center gap-2", display.color)}>
              <span>{display.emoji}</span>
              {category === "new" && "Nauji įgūdžiai"}
              {category === "struggling" && "Stiprinti"}
              {category === "learning" && "Mokytis"}
              {category === "review" && "Pakartota"}
            </h4>
            {changes.map((change) => (
              <div
                key={change.skillKey}
                className={cn(
                  "flex items-center justify-between p-2 rounded-md",
                  display.bgColor,
                  display.borderColor,
                  "border"
                )}
              >
                <span className="font-medium">{change.skillName}</span>
                <span className="text-sm">
                  {change.oldProficiency.toFixed(0)}% &rarr; {change.newProficiency.toFixed(0)}%
                  {change.change !== 0 && (
                    <span
                      className={cn(
                        "ml-2 font-medium",
                        change.change > 0 ? "text-green-600" : "text-red-600"
                      )}
                    >
                      ({change.change > 0 ? "+" : ""}{change.change.toFixed(1)}%)
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
